/**
 * CompactionMarker — the line in the transcript where the context was
 * compacted: "Context compacted — N messages summarised", collapsed by
 * default ("Show summary" / "Hide summary") and quiet — the same chip
 * tokens as the composer's chips, smaller text.
 *
 * Everything above it was folded into the summary the model now continues
 * from; nothing was deleted — the messages stay in the transcript for the
 * person. Because a compaction folds everything up to now, the marker sits
 * at the END of the transcript at the moment it is made and the conversation
 * continues below it. Shared by the regular chat (MessageList) and the Agent
 * of Agents timeline.
 */

import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { activeLocale } from '@/utils/locale';
import './CompactionMarker.css';

export interface CompactionMarkerProps {
  /** The structured summary (the marker row's content). */
  summary: string;
  /** How many earlier messages the summary now stands in for (cumulative). */
  summarizedCount: number;
  /** Start expanded — used right after a live compaction. */
  defaultExpanded?: boolean;
  className?: string;
}

export function CompactionMarker({ summary, summarizedCount, defaultExpanded = false, className }: CompactionMarkerProps) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const locale = activeLocale();
  const countLabel = new Intl.NumberFormat(locale).format(summarizedCount);

  return (
    <div
      className={`compaction-marker${className ? ` ${className}` : ''}`}
      data-testid="compaction-marker"
      data-summarized-count={summarizedCount}
    >
      <div className="compaction-marker-row">
        <div className="compaction-marker-line" />
        <button
          type="button"
          className="compaction-marker-pill"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 7h16M4 12h10M4 17h6" />
          </svg>
          <span>
            {t('chat.compaction.marker', {
              count: summarizedCount,
              countLabel,
              defaultValue: `Context compacted — ${countLabel} messages summarised`,
            })}
          </span>
          <span className="compaction-marker-toggle">
            {expanded
              ? t('chat.compaction.hideSummary', { defaultValue: 'Hide summary' })
              : t('chat.compaction.showSummary', { defaultValue: 'Show summary' })}
          </span>
        </button>
        <div className="compaction-marker-line" />
      </div>
      {expanded && (
        <div className="compaction-marker-summary" dir="auto" data-testid="compaction-summary">
          <div className="compaction-marker-summary-note">
            {t('chat.compaction.note', {
              defaultValue: 'The agent continues from this summary; the earlier messages stay above for you.',
            })}
          </div>
          <pre className="compaction-marker-summary-text">{summary}</pre>
        </div>
      )}
    </div>
  );
}

export default CompactionMarker;
